<template>
    <div class="agent-editor-wrapper">
        <div class="header-section">
            <div class="back-button" @click="goBack">
                <i class="material-icons">arrow_back</i>
                <span>{{ $t('voiceagent.back') }}</span>
            </div>
            <div class="title-wrapper">
                <h1 class="main-title">{{ agent ? agent.agent_name : $t('voiceagent.voiceAgent') }}</h1>
            </div>
            <div class="metrics-wrapper" v-if="agent">
                <div v-if="agent.llm" class="metric-item prompt-token-metric" style="margin-right: 16px; position: relative;">
                    <div
                        class="prompt-token-block"
                        :class="{ 'prompt-token-block--warn-hover': promptTokenOverSurchargeThreshold }"
                    >
                        <span
                            class="prompt-token-count"
                            :class="{ 'prompt-token-count--warn': promptTokenOverSurchargeThreshold }"
                            >{{ $t("voiceagent.promptTokenCount", { count: promptTokenCount }) }}</span
                        >
                        <div v-if="promptTokenOverSurchargeThreshold" class="prompt-token-warn">
                            <i class="material-icons prompt-token-warn__icon" aria-hidden="true">warning</i>
                            <div class="prompt-token-warn__panel" role="tooltip">
                                <p class="prompt-token-warn__text">
                                    {{ $t("voiceagent.promptTokenWarnHallucination") }}
                                </p>
                                <p class="prompt-token-warn__text">
                                    {{ $t("voiceagent.promptTokenWarnSurcharge") }}
                                </p>
                                <div class="prompt-token-warn__divider"></div>
                                <div class="prompt-token-warn__price-row">
                                    <span class="prompt-token-warn__label">{{
                                        $t("voiceagent.promptTokenTooltipBasePricing")
                                    }}</span>
                                    <span class="prompt-token-warn__price"
                                        >${{ formattedLlmBasePerMin
                                        }}{{ $t("voiceagent.perMinuteAbbrev") }}</span
                                    >
                                </div>
                                <div class="prompt-token-warn__estimated">
                                    {{
                                        $t("voiceagent.promptTokenTooltipEstimatedTokens", {
                                            count: promptTokenCount,
                                        })
                                    }}
                                </div>
                                <div class="prompt-token-warn__price-row prompt-token-warn__price-row--emphasis">
                                    <span></span>
                                    <span class="prompt-token-warn__price prompt-token-warn__price--accent"
                                        >${{ formattedLlmDisplayPerMin
                                        }}{{ $t("voiceagent.perMinuteAbbrev") }}</span
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="metric-item" style="margin-right: 16px; position: relative;">
                    <a-tooltip placement="bottom">
                        <template slot="title">
                            <div><b>{{ $t('voiceagent.pricePerMinute') }}</b></div>
                            <div>{{ $t('voiceagent.total') }}: <b>${{ totalPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</b></div>
                            <div style="margin-top: 6px;">
                                <div>{{ $t('voiceagent.pricingLlmCost') }}: ${{ llmPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</div>
                                <div>{{ $t('voiceagent.pricingVoiceInfra') }}: ${{ voiceInfraPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</div>
                                <div v-if="!isRealtimeModel">{{ $t('voiceagent.pricingTtsCost') }}: ${{ ttsPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</div>
                                <div>{{ $t('voiceagent.pricingTelephonyCost') }}: ${{ telephonyPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</div>
                                <div v-if="hasKnowledgeBase">{{ $t('voiceagent.knowledgeBase') }}: ${{ kbPricePerMin.toFixed(4) }}{{ $t('voiceagent.perMinuteAbbrev') }}</div>
                            </div>
                        </template>
                        <span style="font-weight: 500; cursor: pointer;">
                            <i class="material-icons" style="vertical-align: middle; color: #1890ff;">attach_money</i>
                            <span style="vertical-align: middle;">${{ totalPricePerMin.toFixed(3) }}{{ $t('voiceagent.perMinuteAbbrev') }}</span>
                        </span>
                    </a-tooltip>
                </div>
                <div class="metric-item" style="position: relative;">
                    <a-tooltip placement="bottom">
                        <template slot="title">
                            <div><b>{{ $t('voiceagent.estimatedLatency') }}</b></div>
                            <div>{{ $t('voiceagent.total') }}: <b>{{ totalLatencyText }}</b></div>
                            <div style="margin-top: 6px;">
                                <div v-if="!isRealtimeModel">{{ $t('voiceagent.latencyTts') }}: 450 ms</div>
                                <div>{{ $t('voiceagent.latencyLlm') }}: {{ llmLatencyText }}</div>
                                <div>{{ $t('voiceagent.transcription') }}: 100-300 ms</div>
                                <div>{{ $t('voiceagent.latencyTelephony') }}: 100 ms</div>
                                <div v-if="hasKnowledgeBase">{{ $t('voiceagent.knowledgeBase') }}: 75-125 ms</div>
                            </div>
                        </template>
                        <span style="font-weight: 500; cursor: pointer;">
                            <i class="material-icons" style="vertical-align: middle; color: #faad14;">timer</i>
                            <span style="vertical-align: middle;">{{ totalLatencyText }}</span>
                        </span>
                    </a-tooltip>
                </div>
            </div>
        </div>

        <a-spin :spinning="loading">
            <div class="custom-tabs">
                <div class="tabs-header">
                    <div
                        v-for="tab in tabs"
                        :key="tab.key"
                        class="tab-item"
                        :class="{ active: currentTab === tab.key }"
                        @click="currentTab = tab.key"
                    >
                        <span>{{ tab.label }}</span>
                        <span v-if="tab.key === 'web-agent'" class="beta-ribbon">{{ $t('voiceagent.beta') }}</span>
                    </div>
                </div>
                <div class="tab-content">
                    <AgentTab
                        v-if="currentTab === 'agent'"
                        :agent="agent"
                        @update="updateAgent"
                        @llm-updated="handleLLMUpdated"
                    />
                    <FunctionsTab v-if="currentTab === 'functions'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <KnowledgeBaseTab v-if="currentTab === 'knowledge-base'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <SpeechSettingsTab v-if="currentTab === 'speech-settings'" :agent="agent" @update="updateAgent" />
                    <CallSettingsTab v-if="currentTab === 'call-settings'" :agent="agent" @update="updateAgent" />
                    <SecurityFallbackSettingsTab v-if="currentTab === 'security-fallback'" :agent="agent" @update="updateAgent" @llm-updated="handleLLMUpdated" />
                    <WebAgentTab v-if="currentTab === 'web-agent'" :agent="agent" @update="updateAgent" />
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../../api/voiceAgent";
import AgentTab from "./tabs/AgentTab.vue";
import FunctionsTab from "./tabs/FunctionsTab.vue";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab.vue";
import SpeechSettingsTab from "./tabs/SpeechSettingsTab.vue";
import CallSettingsTab from "./tabs/CallSettingsTab.vue";
import SecurityFallbackSettingsTab from "./tabs/SecurityFallbackSettingsTab.vue";
import WebAgentTab from "./tabs/WebAgentTab.vue";
import {
    getElevenLabsVoiceModelUsdPerMinute,
    RETELL_BASE_PRICES,
    RETELL_LIST_MULTIPLIER,
} from "../../../../../../utils/voiceAgentCustomerLlmRate";
import {
    countVoiceAgentPromptTokensLite,
    promptTokensListPriceMultiplier,
    PROMPT_LIST_PRICE_CHUNK_TOKENS,
} from "../../../../../../utils/openaiPromptTokens";

export default {
    name: "AgentEditor",
    components: {
        AgentTab,
        FunctionsTab,
        KnowledgeBaseTab,
        SpeechSettingsTab,
        CallSettingsTab,
        SecurityFallbackSettingsTab,
        WebAgentTab
    },
    props: {
        channelId: {
            type: String,
            required: true
        },
        agentId: {
            type: String,
            required: true
        },
        activeTab: {
            type: String,
            default: "agent"
        }
    },
    data() {
        return {
            loading: false,
            agent: null,
            currentTab: this.activeTab,
            // LLMConfiguration.vue modelOptions ile defaultPrice / fastPrice / hasFastTier aynı (latency yalnızca bu ekranda tahmini gösterim)
            llmModelOptions: [
                { id: "gpt-5.4", defaultPrice: 0.08 * 1.5, fastPrice: 0.16 * 1.5, latency: "500-700 ms", hasFastTier: true },
                { id: "gpt-5.4-mini", defaultPrice: 0.036 * 1.5, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gpt-5.4-nano", defaultPrice: 0.01 * 1.5, fastPrice: null, latency: "300-450 ms", hasFastTier: false },
                { id: "gpt-5.2", defaultPrice: 0.056 * 1.5, fastPrice: 0.112 * 1.5, latency: "500-700 ms", hasFastTier: true },
                { id: "gpt-5.1", defaultPrice: 0.04 * 1.5, fastPrice: 0.08 * 1.5, latency: "500-700 ms", hasFastTier: true },
                { id: "gpt-5", defaultPrice: 0.04 * 1.5, fastPrice: 0.08 * 1.5, latency: "500-700 ms", hasFastTier: true },
                { id: "gpt-5-mini", defaultPrice: 0.012 * 1.5, fastPrice: 0.024 * 1.5, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-5-nano", defaultPrice: 0.003 * 1.5, fastPrice: null, latency: "300-450 ms", hasFastTier: false },
                { id: "gpt-4.1", defaultPrice: 0.045 * 1.5, fastPrice: 0.0675 * 1.5, latency: "500-800 ms", hasFastTier: true },
                { id: "gpt-4.1-mini", defaultPrice: 0.016 * 1.5, fastPrice: 0.024 * 1.5, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-4.1-nano", defaultPrice: 0.004 * 1.5, fastPrice: 0.006 * 1.5, latency: "350-500 ms", hasFastTier: true },
                { id: "gpt-realtime-1.5", defaultPrice: 0.345 * 1.5, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "gpt-realtime", defaultPrice: 0.345 * 1.5, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "gpt-realtime-mini", defaultPrice: 0.07 * 1.5, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "claude-4.6-sonnet", defaultPrice: 0.08 * 1.5, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "claude-4.5-sonnet", defaultPrice: 0.08 * 1.5, fastPrice: null, latency: "650-800 ms", hasFastTier: false },
                { id: "claude-4.5-haiku", defaultPrice: 0.025 * 1.5, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gemini-3.0-flash", defaultPrice: 0.027 * 1.5, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gemini-2.5-flash", defaultPrice: 0.035 * 1.5, fastPrice: null, latency: "350-500 ms", hasFastTier: false },
                { id: "gemini-2.5-flash-lite", defaultPrice: 0.006 * 1.5, fastPrice: null, latency: "200-350 ms", hasFastTier: false },
            ],
        };
    },
    computed: {
        tabs() {
            return [
                { key: 'agent', label: this.$t('voiceagent.agent') },
                { key: 'functions', label: this.$t('voiceagent.functions') },
                { key: 'knowledge-base', label: this.$t('voiceagent.knowledgeBase') },
                { key: 'speech-settings', label: this.$t('voiceagent.speechSettingsTitle') },
                { key: 'call-settings', label: this.$t('voiceagent.callSettings') },
                { key: 'security-fallback', label: this.$t('voiceagent.securityFallbackSettings') },
                { key: 'web-agent', label: this.$t('voiceagent.webAgent') },
            ];
        },
        llmModelId() {
            if (!this.agent || !this.agent.llm) return null;
            // AgentTab'daki modelOptions ile uyumlu id'yi bulmak için
            return this.agent.llm.model;
        },
        llmModelInfo() {
            if (!this.llmModelId) return null;
            var normalized = this.normalizeLlmModelId(this.llmModelId);
            return this.llmModelOptions.find(function (m) { return m.id === normalized; }) || null;
        },
        promptTokenCount() {
            if (!this.agent || !this.agent.llm) {
                return 0;
            }
            return countVoiceAgentPromptTokensLite(
                this.agent.llm.general_prompt,
                this.agent.llm.model,
                this.agent.llm
            );
        },
        promptTokenOverSurchargeThreshold() {
            return this.promptTokenCount > PROMPT_LIST_PRICE_CHUNK_TOKENS;
        },
        formattedLlmBasePerMin() {
            var n = Number(this.baseLlmCustomerPricePerMin);
            return (isNaN(n) ? 0 : n).toFixed(3);
        },
        formattedLlmDisplayPerMin() {
            var n = Number(this.llmPricePerMin);
            return (isNaN(n) ? 0 : n).toFixed(3);
        },
        promptListPriceMultiplier() {
            var modelId = "";
            if (this.agent && this.agent.llm) {
                modelId = this.agent.llm.model || "";
            }
            var tokens = 0;
            if (this.agent && this.agent.llm) {
                tokens = countVoiceAgentPromptTokensLite(
                    this.agent.llm.general_prompt,
                    modelId,
                    this.agent.llm
                );
            }
            return promptTokensListPriceMultiplier(tokens);
        },
        rawLlmPricePerMin() {
            if (!this.llmModelInfo) return 0;

            // Fast tier kullanılıyor mu kontrol et
            var isFastTier = this.agent && this.agent.llm && this.agent.llm.model_high_priority;

            if (isFastTier && this.llmModelInfo.hasFastTier && this.llmModelInfo.fastPrice) {
                return this.llmModelInfo.fastPrice;
            }

            return this.llmModelInfo.defaultPrice;
        },
        baseLlmCustomerPricePerMin() {
            return this.rawLlmPricePerMin || 0;
        },
        llmPricePerMin() {
            if (!this.rawLlmPricePerMin) return 0;
            return this.rawLlmPricePerMin * this.promptListPriceMultiplier;
        },
        voiceInfraPricePerMin() {
            return RETELL_BASE_PRICES.voiceInfra * RETELL_LIST_MULTIPLIER;
        },
        ttsPricePerMin() {
            var raw = 0;
            if (this.agent) {
                raw = getElevenLabsVoiceModelUsdPerMinute(this.agent.voice_model);
            } else {
                raw = getElevenLabsVoiceModelUsdPerMinute(null);
            }
            return raw * RETELL_LIST_MULTIPLIER;
        },
        telephonyPricePerMin() {
            return RETELL_BASE_PRICES.telephonyTwilio * RETELL_LIST_MULTIPLIER;
        },
        kbPricePerMin() {
            if (!this.hasKnowledgeBase) return 0;
            return RETELL_BASE_PRICES.knowledgeBase * RETELL_LIST_MULTIPLIER;
        },
        hasKnowledgeBase() {
            return this.agent && this.agent.llm &&
                Array.isArray(this.agent.llm.knowledge_base_ids) &&
                this.agent.llm.knowledge_base_ids.length > 0;
        },
        isRealtimeModel() {
            var modelId = (this.agent && this.agent.llm && this.agent.llm.model) || "";
            return modelId.indexOf("realtime") !== -1;
        },
        totalPricePerMin() {
            var total = this.llmPricePerMin + this.voiceInfraPricePerMin + this.telephonyPricePerMin;
            if (!this.isRealtimeModel) {
                total += this.ttsPricePerMin;
            }
            if (this.hasKnowledgeBase) {
                total += this.kbPricePerMin;
            }
            return total;
        },
        llmLatencyText() {
            return this.llmModelInfo ? this.llmModelInfo.latency : "-";
        },
        totalLatencyText() {
            if (!this.llmLatencyText || this.llmLatencyText === "-") return "-";
            var llm = this.llmLatencyText.match(/(\d+)-(\d+)/);
            var llmMin = llm ? parseInt(llm[1], 10) : 0;
            var llmMax = llm ? parseInt(llm[2], 10) : 0;
            var ttsMin = this.isRealtimeModel ? 0 : 450;
            var ttsMax = this.isRealtimeModel ? 0 : 450;
            var kbMin = this.hasKnowledgeBase ? 75 : 0;
            var kbMax = this.hasKnowledgeBase ? 125 : 0;
            var min = ttsMin + llmMin + 100 + 100 + kbMin;
            var max = ttsMax + llmMax + 300 + 100 + kbMax;
            return min + "-" + max + " ms";
        },
    },
    watch: {
        currentTab(newTab) {
            // Tab değiştiğinde URL'deki query parametresini güncelle
            this.$router.push({
                path: this.$route.path,
                query: {
                    agent_id: this.agentId,
                    editor_tab: newTab
                }
            });
        },
        activeTab(newTab) {
            this.currentTab = newTab;
        }
    },
    methods: {
        normalizeLlmModelId(apiModelId) {
            var id = String(apiModelId || "").trim().toLowerCase();
            if (id.indexOf("/") !== -1) {
                id = id.replace(/^[^/]+\//, "");
            }
            var legacyMap = {
                "claude-3.7-sonnet": "claude-4.5-sonnet",
                "gemini-2.0-flash": "gemini-2.5-flash",
                "gemini-2.0-flash-lite": "gemini-2.5-flash-lite",
            };
            return legacyMap[id] || id;
        },
        async fetchAgent() {
            try {
                this.loading = true;
                const agent = await VOICE_AGENT_API.agents.get(this.agentId);
                this.agent = agent;
            } catch (error) {
                console.error("Ajan çekilirken hata oluştu:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingAgent"));
            } finally {
                this.loading = false;
            }
        },
        updateAgent(updatedData) {
            this.loading = true;
            VOICE_AGENT_API.agents.update(this.agentId, updatedData)
                .then(updatedAgent => {
                    this.agent = updatedAgent;
                    this.$message.success(this.$t("voiceagent.agentUpdatedSuccessfully"));
                })
                .catch(error => {
                    console.error("Ajan güncellenirken hata oluştu:", error);
                    this.$message.error(this.$t("voiceagent.agentUpdateFailedMessage"));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleLLMUpdated(updatedLLM) {
            // PATCH yanıtı genelde kısmi döner; tüm nesneyi değiştirmek begin_message / general_prompt vb. alanları siler.
            // Mevcut llm ile birleştirerek AgentTab'daki karşılama modu ve metin senkronunun bozulmasını önle.
            if (this.agent && this.agent.llm && updatedLLM && typeof updatedLLM === "object") {
                var keys = Object.keys(updatedLLM);
                for (var i = 0; i < keys.length; i++) {
                    var key = keys[i];
                    var val = updatedLLM[key];
                    // undefined: yanıtta yok sayılır (mevcut llm korunur). null: sunucu alanı temizlediyse uygulanır.
                    if (val !== undefined) {
                        this.agent.llm[key] = val;
                    }
                }
                this.$message.success(this.$t("voiceagent.llmPromptUpdated"));
            }
        },
        goBack() {
            // Üst bileşene geri düğmesi tıklandığını bildir
            this.$emit('back');
        }
    },
    created() {
        this.fetchAgent();
    }
};
</script>

<style scoped lang="scss">
.agent-editor-wrapper {
    display: flex;
    flex-direction: column;
    padding: 0 20px;

    .header-section {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        .metrics-wrapper {
            display: flex;
            align-items: center;
            margin-left: auto;
        }
        .metric-item {
            font-size: 16px;
        }

        .prompt-token-metric {
            .prompt-token-block {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .prompt-token-count {
                font-size: 14px;
                color: rgba(0, 0, 0, 0.55);
                white-space: nowrap;
            }

            .prompt-token-count--warn {
                color: #cf1322;
                font-weight: 600;
            }

            .prompt-token-block--warn-hover {
                cursor: default;
            }

            .prompt-token-block--warn-hover:hover .prompt-token-warn__panel {
                opacity: 1;
                visibility: visible;
                pointer-events: auto;
                transform: translateX(-50%) translateY(0);
            }

            .prompt-token-warn {
                position: relative;
                display: inline-flex;
                align-items: center;
            }

            .prompt-token-warn::after {
                content: "";
                position: absolute;
                left: 50%;
                bottom: 0;
                width: 48px;
                height: 14px;
                transform: translateX(-50%) translateY(100%);
            }

            .prompt-token-warn__icon {
                color: #cf1322;
                font-size: 20px;
                line-height: 1;
                cursor: default;
            }

            .prompt-token-warn__panel {
                position: absolute;
                left: 50%;
                top: 100%;
                margin-top: 8px;
                transform: translateX(-50%) translateY(4px);
                min-width: 288px;
                max-width: min(360px, 92vw);
                padding: 14px 16px;
                background: linear-gradient(145deg, #ffffff 0%, #faf7f5 100%);
                border: 1px solid rgba(207, 19, 34, 0.22);
                border-radius: 10px;
                box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(207, 19, 34, 0.08);
                z-index: 1200;
                opacity: 0;
                visibility: hidden;
                pointer-events: none;
                transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
            }

            .prompt-token-warn__panel::before {
                content: "";
                position: absolute;
                left: 50%;
                bottom: 100%;
                transform: translateX(-50%);
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-bottom: 8px solid rgba(207, 19, 34, 0.22);
            }

            .prompt-token-warn__text {
                margin: 0 0 10px;
                font-size: 12px;
                line-height: 1.45;
                color: rgba(0, 0, 0, 0.72);
            }

            .prompt-token-warn__text:last-of-type {
                margin-bottom: 0;
            }

            .prompt-token-warn__divider {
                height: 1px;
                margin: 12px 0;
                background: linear-gradient(
                    90deg,
                    rgba(207, 19, 34, 0.35),
                    rgba(0, 0, 0, 0.06) 60%,
                    transparent
                );
            }

            .prompt-token-warn__price-row {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                gap: 12px;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.78);
            }

            .prompt-token-warn__price-row--emphasis {
                margin-top: 6px;
            }

            .prompt-token-warn__label {
                font-weight: 600;
                color: rgba(0, 0, 0, 0.82);
            }

            .prompt-token-warn__price {
                font-family: "SF Mono", "Monaco", "Menlo", "Consolas", monospace;
                font-weight: 600;
                color: rgba(0, 0, 0, 0.88);
            }

            .prompt-token-warn__price--accent {
                font-size: 13px;
                color: #cf1322;
            }

            .prompt-token-warn__estimated {
                margin-top: 10px;
                font-size: 11px;
                letter-spacing: 0.02em;
                text-transform: uppercase;
                color: rgba(0, 0, 0, 0.45);
            }
        }
        .back-button {
            display: flex;
            align-items: center;
            cursor: pointer;
            margin-right: 20px;
            color: #22272d;

            .material-icons {
                font-size: 20px;
                margin-right: 5px;
            }
        }

        .title-wrapper {
            display: flex;
            align-items: center;

            .main-title {
                font-size: 24px;
                font-weight: 500;
                margin: 0;
            }
        }
    }

    .custom-tabs {
        margin-top: 10px;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);

        .tabs-header {
            display: flex;
            align-items: center;
            border-bottom: 1px solid #f0f0f0;
            background: #fafafa;
            position: relative;

            .tab-item {
                padding: 14px 24px;
                cursor: pointer;
                position: relative;
                transition: all 0.3s;
                font-size: 15px;
                color: rgba(0, 0, 0, 0.65);

                &:hover {
                    color: #22272d;
                }

                &.active {
                    color: #22272d;
                    background: white;
                    border-bottom: none;
                    font-weight: 500;

                    &:after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        height: 2px;
                        background: #22272d;
                    }
                }

                .beta-ribbon {
                    background-color: #ff4d4f;
                    color: #fff;
                    font-size: 12px;
                    padding: 2px 6px;
                    border-radius: 4px;
                    margin-left: 8px;
                    font-weight: bold;
                    position: relative;
                    top: -1px;
                }
            }
        }

        .tab-content {
            padding: 24px;
            min-height: 300px;
        }
    }
}
</style>
